<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex flex-wrap justify-between items-end gap-3 mb-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">{{ $t('smm.networks.title') }}</h2>
        <p class="text-sm text-gray-500 mt-1">{{ $t('smm.networks.subtitle') }}</p>
      </div>
      <button
        @click="openForm()"
        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
      >
        {{ $t('smm.networks.add') }}
      </button>
    </div>

    <div v-if="loading" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>
    <div v-else-if="loadError" class="p-8 text-center text-red-600 text-sm">
      {{ loadError }}
    </div>
    <div v-else-if="!networks.length" class="p-8 text-center text-gray-500 bg-white rounded-lg shadow-md">
      {{ $t('smm.networks.empty') }}
    </div>
    <div
      v-else
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    >
      <article
        v-for="network in networks"
        :key="network.id"
        class="bg-white rounded-lg shadow-md border border-gray-100 p-4 flex flex-col hover:border-blue-200 hover:shadow-lg transition"
      >
        <div class="flex items-start justify-between gap-2 mb-2">
          <button
            type="button"
            class="font-semibold text-gray-800 hover:text-gray-950 text-lg leading-snug min-w-0 text-left"
            @click="openForm(network)"
          >
            {{ network.name }}
          </button>
          <div class="shrink-0 flex items-center gap-1.5">
            <button
              type="button"
              class="inline-flex items-center justify-center w-7 h-7 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
              :title="$t('smm.ideas.open')"
              :aria-label="$t('smm.ideas.open')"
              @click.stop="openIdeas(network)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 18v.01M9.75 16.5c.5.75 1.3 1.5 2.25 1.5s1.75-.75 2.25-1.5M8.25 10.5a3.75 3.75 0 117.5 0c0 1.35-.7 2.3-1.5 3.05-.5.45-.9.85-1.05 1.45h-2.4c-.15-.6-.55-1-1.05-1.45-.8-.75-1.5-1.7-1.5-3.05z" />
              </svg>
            </button>
            <span
              v-if="!network.is_active"
              class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-50 text-red-600"
            >
              {{ $t('smm.networks.badge_inactive') }}
            </span>
            <span
              v-if="hasBasePrompt(network)"
              class="inline-flex items-center justify-center w-6 h-6 rounded-md bg-green-50 text-green-700 border border-green-200"
              :title="$t('smm.networks.base_prompt_ready')"
              :aria-label="$t('smm.networks.base_prompt_ready')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </span>
          </div>
        </div>

        <p class="text-xs text-gray-500 mb-2">
          {{ network.slug }}
          <span v-if="network.username" class="ml-1">@{{ network.username }}</span>
          <span v-if="network.has_api" class="ml-1 text-green-600">{{ $t('smm.networks.badge_api') }}</span>
        </p>

        <div v-if="network.shop || network.platform" class="flex flex-wrap items-center gap-1.5 mb-2">
          <span
            v-if="network.shop"
            class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border"
            :style="shopBadgeStyle(network.shop)"
          >
            <span
              class="w-2 h-2 rounded-full shrink-0 border border-black/10"
              :style="{ backgroundColor: network.shop.color || '#111827' }"
            />
            {{ network.shop.name }}
          </span>
          <span
            v-if="network.platform"
            class="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
          >
            {{ platformLabel(network.platform) }}
          </span>
        </div>

        <a
          v-if="network.profile_url"
          :href="network.profile_url"
          target="_blank"
          rel="noopener"
          class="text-xs text-gray-700 hover:underline break-all line-clamp-2 mb-2"
        >
          {{ network.profile_url }}
        </a>

        <p v-if="network.description" class="text-sm text-gray-600 line-clamp-3 mb-3 flex-1">
          {{ network.description }}
        </p>
        <div v-else class="flex-1"></div>

        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span
            v-if="network.followers_count != null"
            class="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-400"
          >
            {{ $t('smm.networks.followers_label', { count: formatFollowers(network.followers_count) }) }}
          </span>
        </div>

        <p class="text-xs text-gray-400 mb-3">
          {{ $t('smm.networks.stats', {
            prompts: network.prompt_templates_count ?? 0,
            tasks: network.content_items_count ?? 0,
            ideas: network.ideas_count ?? 0,
          }) }}
        </p>

        <div class="flex gap-2 pt-3 border-t border-gray-100">
          <button
            @click="openForm(network)"
            class="flex-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 py-1.5 rounded-lg"
          >
            {{ $t('common.edit') }}
          </button>
          <button
            @click="remove(network)"
            class="flex-1 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 py-1.5 rounded-lg"
          >
            {{ $t('common.delete') }}
          </button>
        </div>
      </article>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      @click.self="closeForm"
    >
      <div class="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-6xl p-4 sm:p-6 max-h-[92vh] overflow-y-auto shadow-xl">
        <h3 class="font-semibold text-lg mb-4">{{ editing ? $t('common.edit_title') : $t('smm.networks.new_title') }}</h3>
        <form @submit.prevent="save" class="space-y-4">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 lg:items-start">
            <div class="space-y-3">
              <div>
                <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
                <input v-model="form.name" required class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm" />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.slug') }}</label>
                <input
                  v-model="form.slug"
                  class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm"
                  :placeholder="$t('smm.networks.slug_placeholder')"
                />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.shop') }}</label>
                <select v-model="form.shop_id" class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm bg-white">
                  <option :value="null">{{ $t('smm.networks.shop_none') }}</option>
                  <option v-for="shop in shops" :key="shop.id" :value="shop.id">
                    {{ shop.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.platform') }}</label>
                <select v-model="form.platform_id" class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm bg-white">
                  <option :value="null">{{ $t('smm.networks.platform_none') }}</option>
                  <option v-for="platform in platforms" :key="platform.id" :value="platform.id">
                    {{ platform.name }}
                  </option>
                </select>
              </div>
              <label class="flex items-center gap-2 text-sm py-1">
                <input type="checkbox" v-model="form.is_active" class="h-4 w-4" />
                {{ $t('smm.networks.is_active') }}
              </label>
            </div>

            <div class="space-y-3">
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.profile_url') }}</label>
                <input
                  v-model="form.profile_url"
                  type="url"
                  class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm"
                  :placeholder="$t('smm.networks.profile_url_placeholder')"
                />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.username') }}</label>
                <input v-model="form.username" class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm" />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.followers_count') }}</label>
                <input
                  v-model.number="form.followers_count"
                  type="number"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm"
                />
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('smm.networks.password_hint') }}</label>
                <input v-model="form.password_hint" class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm" />
                <p class="text-xs text-gray-400 mt-1">{{ $t('smm.networks.password_hint_help') }}</p>
              </div>
              <div>
                <label class="text-sm text-gray-600">{{ $t('common.description') }}</label>
                <textarea v-model="form.description" rows="3" class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm" />
              </div>
            </div>

            <div class="flex flex-col min-h-0">
              <label class="text-sm text-gray-600">{{ $t('smm.networks.base_prompt') }}</label>
              <textarea
                v-model="form.base_prompt"
                rows="14"
                class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm resize-y min-h-[16rem] lg:min-h-[22rem] flex-1"
                :placeholder="$t('smm.networks.base_prompt_placeholder')"
              />
              <p class="text-xs text-gray-400 mt-1">{{ $t('smm.networks.base_prompt_help') }}</p>
            </div>
          </div>
          <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              @click="closeForm"
              class="w-full sm:w-auto px-4 py-2.5 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              :disabled="saving"
            >
              {{ saving ? $t('common.saving_short') : $t('common.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <network-ideas-modal
      :show="showIdeasModal"
      :network="ideasNetwork"
      @close="closeIdeas"
      @changed="onIdeasChanged"
    />
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'
import NetworkIdeasModal from '@/components/smm/NetworkIdeasModal.vue'

const emptyForm = () => ({
  name: '',
  slug: '',
  shop_id: null,
  platform_id: null,
  description: '',
  base_prompt: '',
  profile_url: '',
  username: '',
  password_hint: '',
  followers_count: null,
  has_api: false,
  is_active: true,
})

export default {
  components: { SmmSubNav, NetworkIdeasModal },
  data: () => ({
    networks: [],
    shops: [],
    platforms: [],
    loading: true,
    loadError: null,
    showModal: false,
    showIdeasModal: false,
    ideasNetwork: null,
    editing: null,
    saving: false,
    formError: null,
    form: emptyForm(),
  }),
  mounted() {
    this.load()
    this.loadShops()
    this.loadPlatforms()
  },
  methods: {
    openIdeas(network) {
      this.ideasNetwork = network
      this.showIdeasModal = true
    },
    closeIdeas() {
      this.showIdeasModal = false
      this.ideasNetwork = null
    },
    async onIdeasChanged() {
      await this.load()
    },
    formatFollowers(n) {
      if (n == null || n === '') return '0'
      return Number(n).toLocaleString(this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US')
    },
    platformLabel(platform) {
      if (!platform) return ''
      if (typeof platform === 'object') return platform.name || platform.slug || ''
      return String(platform)
    },
    hasBasePrompt(network) {
      return String(network?.base_prompt || '').trim().length > 0
    },
    shopBadgeStyle(shop) {
      const bg = shop?.color || '#111827'
      return {
        backgroundColor: `${bg}18`,
        borderColor: bg,
        color: bg,
      }
    },
    async loadShops() {
      try {
        const { data } = await axios.get('/api/v1/smm/shops', { params: { all: 1 } })
        this.shops = (data.data ?? []).map((s) => ({ ...s, id: Number(s.id) }))
      } catch (_) {
        this.shops = []
      }
    },
    async loadPlatforms() {
      try {
        const { data } = await axios.get('/api/v1/smm/platforms')
        this.platforms = (data.data ?? []).map((p) => ({ ...p, id: Number(p.id) }))
      } catch (_) {
        this.platforms = []
      }
    },
    async load() {
      this.loading = true
      this.loadError = null
      try {
        const { data } = await axios.get('/api/v1/smm/social-networks')
        this.networks = data.data ?? []
      } catch (e) {
        this.networks = []
        this.loadError = e.response?.data?.message
          || (e.response?.status === 401 ? this.$t('errors.auth_required') : this.$t('smm.networks.load_error'))
      } finally {
        this.loading = false
      }
    },
    openForm(network = null) {
      this.editing = network
      this.form = network
        ? {
            name: network.name || '',
            slug: network.slug || '',
            shop_id: network.shop_id != null ? Number(network.shop_id) : (network.shop?.id != null ? Number(network.shop.id) : null),
            platform_id: network.platform_id != null
              ? Number(network.platform_id)
              : (network.platform?.id != null ? Number(network.platform.id) : null),
            description: network.description || '',
            base_prompt: network.base_prompt || '',
            profile_url: network.profile_url || '',
            username: network.username || '',
            password_hint: network.password_hint || '',
            followers_count: network.followers_count ?? null,
            has_api: !!network.has_api,
            is_active: network.is_active !== false,
          }
        : emptyForm()
      this.formError = null
      this.showModal = true
    },
    closeForm() {
      this.showModal = false
      this.editing = null
    },
    payload() {
      return {
        ...this.form,
        shop_id: this.form.shop_id === '' || this.form.shop_id == null ? null : Number(this.form.shop_id),
        platform_id: this.form.platform_id === '' || this.form.platform_id == null ? null : Number(this.form.platform_id),
        description: this.form.description || null,
        base_prompt: this.form.base_prompt || null,
        profile_url: this.form.profile_url || null,
        username: this.form.username || null,
        password_hint: this.form.password_hint || null,
        followers_count: this.form.followers_count === '' || this.form.followers_count == null
          ? null
          : Number(this.form.followers_count),
      }
    },
    async save() {
      this.saving = true
      this.formError = null
      try {
        const body = this.payload()
        if (this.editing) {
          await axios.put(`/api/v1/smm/social-networks/${this.editing.id}`, body)
        } else {
          await axios.post('/api/v1/smm/social-networks', body)
        }
        this.closeForm()
        await this.load()
      } catch (e) {
        this.formError = e.response?.data?.message || this.$t('common.save_error')
      } finally {
        this.saving = false
      }
    },
    async remove(network) {
      if (!confirm(this.$t('smm.networks.delete_confirm', { name: network.name }))) return
      await axios.delete(`/api/v1/smm/social-networks/${network.id}`)
      await this.load()
    },
  },
}
</script>
